import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { publicApi } from '../utils/api';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Menu,
  X,
  User,
  Wallet,
  Users,
  ShoppingBag,
  Settings,
  LogOut,
  LayoutDashboard,
  ChevronDown,
  Tag,
  Palette,
  Home,
  HelpCircle,
  DollarSign,
  Info,
  Mail
} from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';

const LOGO_FULL_FALLBACK = "/api/uploads/images/aga-logo-fallback.png";
const LOGO_ICON_FALLBACK = "/api/uploads/images/aga-icon-fallback.png";

/**
 * Fetch the set of published CMS slugs once and cache in module scope.
 * Used by Header + Footer to hide draft/unpublished pages from the menus.
 */
let _publishedSlugsCache = null;
let _publishedSlugsPromise = null;

function usePublishedCmsSlugs() {
  const [slugs, setSlugs] = useState(() => _publishedSlugsCache || new Set());
  useEffect(() => {
    if (_publishedSlugsCache) return;
    if (!_publishedSlugsPromise) {
      _publishedSlugsPromise = publicApi.get('/api/cms/pages')
        .then(({ data }) => {
          const pages = data?.pages || data || [];
          const set = new Set(pages.map(p => p.slug).filter(Boolean));
          _publishedSlugsCache = set;
          return set;
        })
        .catch(() => new Set());
    }
    _publishedSlugsPromise.then(setSlugs);
  }, []);
  return slugs;
}

export function Header() {
  const { user, logout, isAdmin } = useAuth();
  const { logoUrl, iconUrl, siteName } = useSiteConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const publishedSlugs = usePublishedCmsSlugs();

  const LOGO_FULL = logoUrl || LOGO_FULL_FALLBACK;
  const LOGO_ICON = iconUrl || LOGO_ICON_FALLBACK;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = user ? (
    isAdmin ? [
      { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
      { href: '/admin/users', label: 'Users', icon: Users },
      { href: '/admin/portfolio', label: 'Portfolio', icon: Wallet },
      { href: '/admin/services', label: 'Services', icon: ShoppingBag },
      { href: '/admin/plans', label: 'Plans', icon: Tag },
      { href: '/admin/settings', label: 'Settings', icon: Settings },
    ] : [
      { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { href: '/wallet', label: 'Wallet', icon: Wallet },
      { href: '/merchant-partners', label: 'Marketplace', icon: ShoppingBag },
      { href: '/page/insurance', label: 'Insurance', icon: Info, cmsSlug: 'insurance' },
    ].filter(item => !item.cmsSlug || publishedSlugs.has(item.cmsSlug))
  ) : [
    { href: '/', label: 'Home', icon: Home },
    { href: '/marketplace', label: 'Marketplace', icon: ShoppingBag },
    { href: '/pricing', label: 'Pricing', icon: DollarSign },
    { href: '/page/about', label: 'About', icon: Info, cmsSlug: 'about' },
    { href: '/page/insurance', label: 'Insurance', icon: Info, cmsSlug: 'insurance' },
    { href: '/page/faq', label: 'FAQ', icon: HelpCircle, cmsSlug: 'faq' },
    { href: '/page/contact', label: 'Contact', icon: Mail, cmsSlug: 'contact' },
  ].filter(item => !item.cmsSlug || publishedSlugs.has(item.cmsSlug));

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink">
            <img src={LOGO_ICON} alt="AGA" className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0" />
            <span className="font-outfit font-semibold text-[13px] sm:text-base lg:text-lg text-white truncate tracking-tight uppercase">
              {siteName || 'ALL GLOBAL ACCESS'}
            </span>
          </Link>

          {/* Desktop Navigation — visible for everyone (guests + logged-in) */}
          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`flex items-center gap-1.5 px-2.5 xl:px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname === link.href
                    ? 'text-[#00E5FF] bg-white/5'
                    : 'text-[#A0B4CC] hover:text-white hover:bg-white/5'
                }`}
                data-testid={`desktop-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-1 sm:gap-4">
            {user ? (
              <>
                {/* User menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-2 text-white"
                      data-testid="user-menu-btn"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#041A3B] border border-white/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-[#00E5FF]" />
                      </div>
                      <span className="hidden sm:block text-sm">{user.name}</span>
                      <ChevronDown className="w-4 h-4 text-[#A0B4CC]" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-[#041A3B] border-white/10">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-[#A0B4CC]">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem 
                      className="text-[#A0B4CC] hover:text-white hover:bg-white/5 cursor-pointer"
                      onClick={() => navigate('/settings')}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem 
                      className="text-[#FF1744] hover:bg-white/5 cursor-pointer"
                      onClick={handleLogout}
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="hidden sm:flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" className="text-white" data-testid="login-nav-btn">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-primary" data-testid="register-nav-btn">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile menu button — visible on phones + tablets (desktop nav appears at lg+) */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/10" data-testid="mobile-nav-panel">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === link.href
                      ? 'text-[#00E5FF] bg-white/5'
                      : 'text-[#A0B4CC] hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}

              {/* Auth CTAs for guests on mobile */}
              {!user && (
                <div className="pt-3 mt-2 border-t border-white/10 flex flex-col gap-2 px-2">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5" data-testid="mobile-login-btn">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full btn-primary" data-testid="mobile-register-btn">
                      Get Started
                    </Button>
                  </Link>
                </div>
              )}

              {/* Theme toggle accessible from the mobile menu too */}
              <div className="pt-3 mt-2 border-t border-white/10 px-2">
                <ThemeToggle className="w-full justify-center" />
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export function Footer() {
  const { iconUrl, siteName } = useSiteConfig();
  const LOGO_ICON = iconUrl || LOGO_ICON_FALLBACK;
  const publishedSlugs = usePublishedCmsSlugs();
  const showSlug = (slug) => publishedSlugs.has(slug);

  return (
    <footer className="relative bg-[#000B18] border-t border-white/10 overflow-hidden">
      {/* Editorial top stripe with year + brand mark */}
      <div className="border-b border-white/[0.06] bg-[#041A3B]/70 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON} alt="AGA" className="h-9 w-9" />
            <div>
              <div className="font-outfit font-semibold text-white text-sm sm:text-base tracking-tight uppercase">
                {siteName || 'All Global Access'}
              </div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#7A8FA8] uppercase mt-0.5">
                Est. 2025 · Manila
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-[#7A8FA8] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" />
            Membership Live
          </div>
        </div>
      </div>

      {/* Body columns */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <h3 className="font-outfit text-2xl sm:text-3xl text-white leading-tight tracking-tight max-w-sm">
              A single membership. <br />
              <span className="text-luxe">Unlimited doors opened.</span>
            </h3>
            <p className="text-[#A0B4CC] text-sm mt-5 max-w-md leading-relaxed">
              Combining member-exclusive service access, referral commissions, and a
              transparent participation-value ledger.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-1.5 text-[#00E5FF] hover:text-white text-sm font-medium transition-colors group"
              >
                Become a member
                <span className="transition-transform group-hover:translate-x-0.5">→</span>
              </Link>
              <span className="text-white/10">|</span>
              <Link to="/marketplace" className="text-[#A0B4CC] hover:text-white text-sm transition-colors">
                Browse marketplace
              </Link>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-mono text-[10px] tracking-[0.3em] text-[#FFB800] uppercase mb-5">Explore</h4>
            <ul className="space-y-3">
              <li><Link to="/marketplace" className="text-[#A0B4CC] hover:text-white text-sm transition-colors">Marketplace</Link></li>
              <li><Link to="/pricing" className="text-[#A0B4CC] hover:text-white text-sm transition-colors">Pricing</Link></li>
              {showSlug('about') && <li><Link to="/page/about" className="text-[#A0B4CC] hover:text-white text-sm transition-colors">About</Link></li>}
              {showSlug('insurance') && <li><Link to="/page/insurance" className="text-[#A0B4CC] hover:text-white text-sm transition-colors">Insurance</Link></li>}
              {showSlug('faq') && <li><Link to="/page/faq" className="text-[#A0B4CC] hover:text-white text-sm transition-colors">FAQ</Link></li>}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-mono text-[10px] tracking-[0.3em] text-[#FFB800] uppercase mb-5">Legal &amp; Contact</h4>
            <ul className="space-y-3">
              {showSlug('privacy-policy') && <li><Link to="/page/privacy-policy" className="text-[#A0B4CC] hover:text-white text-sm transition-colors">Privacy Policy</Link></li>}
              {showSlug('terms-of-service') && <li><Link to="/page/terms-of-service" className="text-[#A0B4CC] hover:text-white text-sm transition-colors">Terms of Service</Link></li>}
              {showSlug('contact') && <li><Link to="/page/contact" className="text-[#A0B4CC] hover:text-white text-sm transition-colors">Contact Us</Link></li>}
              <li><Link to="/help" className="text-[#A0B4CC] hover:text-white text-sm transition-colors">Help Center</Link></li>
            </ul>
          </div>
        </div>

        {/* Gold accent divider */}
        <div className="mt-14 sm:mt-20 flex items-center gap-4">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <span className="font-mono text-[10px] tracking-[0.4em] text-[#FFB800] uppercase">◈ AGA</span>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="mt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
          <p className="text-[#5C7599] text-xs sm:text-sm">
            © {new Date().getFullYear()} All Global Access. All rights reserved.
          </p>
          <p className="text-[#5C7599] text-[11px] sm:text-xs max-w-lg md:text-right leading-relaxed">
            Participation-value tracking only. Not a financial institution. Value may fluctuate.
          </p>
        </div>
      </div>
    </footer>
  );
}
